import { useEffect, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Zustand-Farbe für Flächen (gut / mittel / schlecht)
export function flaecheZustandColor(z: string | null): string {
  switch ((z || '').toLowerCase()) {
    case 'gut': return '#2e7d32';
    case 'mittel': return '#f9a825';
    case 'schlecht': return '#c62828';
    default: return '#558b2f';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FlaechenLayer — zeichnet die Polygone als GeoJSON-Layer, eingefärbt nach Zustand.
// Marker liegen pane-bedingt immer darüber, die Flächen verdecken also keine Bäume.
// ─────────────────────────────────────────────────────────────────────────────
export function FlaechenLayer({ flaechen, visible, onFlaecheClick, highlightId }: {
  flaechen: any[];
  visible: boolean;
  onFlaecheClick: (id: string) => void;
  highlightId?: string;
}) {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    if (!visible) return;

    const lg = L.layerGroup();
    let hiLayer: L.GeoJSON | null = null;
    flaechen.forEach(f => {
      if (!f.geometry) return;
      const color = flaecheZustandColor(f.zustand);
      const isHi = !!highlightId && f.id === highlightId;
      const gj = L.geoJSON(f.geometry as any, {
        style: {
          color: isHi ? '#0d3b13' : color,
          weight: isHi ? 4 : 2,
          fillColor: color,
          fillOpacity: isHi ? 0.32 : 0.18,
        },
      });
      gj.on('click', () => onFlaecheClick(f.id));
      gj.bindTooltip(f.name || 'Fläche', { sticky: true, direction: 'top' });
      gj.addTo(lg);
      if (isHi) hiLayer = gj;
    });
    layerRef.current = lg;
    lg.addTo(map);
    if (hiLayer && (hiLayer as L.GeoJSON).bringToFront) (hiLayer as L.GeoJSON).bringToFront();

    return () => { if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; } };
  }, [map, flaechen, visible, onFlaecheClick, highlightId]);

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// FitToFlaeche — zoomt einmalig auf das Polygon der angegebenen Fläche.
// ─────────────────────────────────────────────────────────────────────────────
export function FitToFlaeche({ flaeche }: { flaeche: any }) {
  const map = useMap();
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !flaeche || !flaeche.geometry) return;
    try {
      const bounds = L.geoJSON(flaeche.geometry as any).getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
        done.current = true;
      }
    } catch { /* ignore */ }
  }, [map, flaeche]);

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// DrawFlaeche — Polygon zeichnen: Klick setzt Eckpunkte, Doppelklick schließt ab.
// Liefert beim Abschluss ein GeoJSON-Polygon ([lng,lat], geschlossener Ring).
// ─────────────────────────────────────────────────────────────────────────────
export function DrawFlaeche({ active, onComplete }: {
  active: boolean;
  onComplete: (geojson: any) => void;
}) {
  const map = useMap();
  const pointsRef = useRef<L.LatLng[]>([]);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const redraw = () => {
    if (layerRef.current) map.removeLayer(layerRef.current);
    const lg = L.layerGroup();
    const pts = pointsRef.current;
    if (pts.length > 2) {
      L.polygon(pts, { color: '#1b5e20', weight: 1.5, fillColor: '#2e7d32', fillOpacity: 0.15 }).addTo(lg);
    } else if (pts.length > 1) {
      L.polyline(pts, { color: '#1b5e20', weight: 2, dashArray: '4 4' }).addTo(lg);
    }
    pts.forEach(p => {
      L.circleMarker(p, { radius: 4, color: '#fff', weight: 1.5, fillColor: '#1b5e20', fillOpacity: 1 }).addTo(lg);
    });
    layerRef.current = lg;
    lg.addTo(map);
  };

  const cleanup = () => {
    pointsRef.current = [];
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
  };

  const finish = () => {
    const pts = pointsRef.current;
    if (pts.length < 3) return;
    const ring = pts.map(p => [p.lng, p.lat]);
    ring.push(ring[0]); // Ring schließen
    const geojson = { type: 'Polygon', coordinates: [ring] };
    cleanup();
    onComplete(geojson);
  };

  // Einzelklick = Punkt (mit kleinem Delay, damit Doppelklick nicht 2 Punkte setzt)
  useMapEvents({
    click: (e) => {
      if (!active) return;
      if (clickTimer.current) clearTimeout(clickTimer.current);
      const latlng = e.latlng;
      clickTimer.current = setTimeout(() => {
        pointsRef.current = [...pointsRef.current, latlng];
        redraw();
      }, 230);
    },
    dblclick: () => {
      if (!active) return;
      if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
      finish();
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    if (active) {
      map.doubleClickZoom.disable();
      container.style.cursor = 'crosshair';
    } else {
      map.doubleClickZoom.enable();
      container.style.cursor = '';
      cleanup();
    }
    return () => {
      map.doubleClickZoom.enable();
      container.style.cursor = '';
    };
  }, [active, map]);

  return null;
}
